import React from 'react';
import {
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ZoomIn,
  ZoomOut,
  FullscreenExit,
  RotateLeft,
  RotateRight,
  DragIndicator,
  Lock,
  LockOpen,
  Home,
} from '@mui/icons-material';
import { ZoomControlsProps } from '../../types/imageTransform';
import { Icon } from '@iconify/react';

interface ImageControlBarProps extends ZoomControlsProps {
  onEditReceipt?: () => void;
  onDragStart?: (e: React.MouseEvent) => void;
}

const ImageControlBar: React.FC<ImageControlBarProps> = ({
  onEditReceipt,
  scale,
  isZoomLocked,
  onZoomIn,
  onZoomOut,
  onZoomInStart,
  onZoomOutStart,
  onZoomStop,
  onRotateLeft,
  onRotateRight,
  onReset,
  onToggleLock,
  onReturnHome,
  onDragStart,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: '#2C6CA0',
        color: 'white',
        px: 2,
        py: 1,
        zIndex: 100,
        width: 'calc(100%)',
        cursor: 'grab',
        '&:active': {
          cursor: 'grabbing',
        },
      }}
      onMouseDown={onDragStart}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
        <DragIndicator fontSize="small" />
        <Box sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
          Receipt Image
        </Box>
        <Icon icon="fluent:edit-16-regular" width="18" height="18" style={{ color: 'white', cursor: 'pointer' }} onClick={onEditReceipt} />
      </Box>

      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title={scale >= 5 ? 'Max zoom' : 'Zoom In (hold for continuous)'}>
          <span>
            <IconButton
              size="small"
              onMouseDown={onZoomInStart}
              onMouseUp={onZoomStop}
              onMouseLeave={onZoomStop}
              sx={{ color: 'white' }}
              disabled={scale >= 5}
            >
              <ZoomIn fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={scale <= 0.2 ? 'Min zoom' : 'Zoom Out (hold for continuous)'}>
          <span>
            <IconButton
              size="small"
              onMouseDown={onZoomOutStart}
              onMouseUp={onZoomStop}
              onMouseLeave={onZoomStop}
              sx={{ color: 'white' }}
              disabled={scale <= 0.2}
            >
              <ZoomOut fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Rotate Left">
          <IconButton size="small" onClick={onRotateLeft} sx={{ color: 'white' }}>
            <RotateLeft fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Rotate Right">
          <IconButton size="small" onClick={onRotateRight} sx={{ color: 'white' }}>
            <RotateRight fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset">
          <IconButton size="small" onClick={onReset} sx={{ color: 'white' }}>
            <FullscreenExit fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={isZoomLocked ? 'Unlock Zoom' : 'Lock Zoom'}>
          <IconButton size="small" onClick={onToggleLock} sx={{ color: 'white' }}>
            {isZoomLocked ? <LockOpen fontSize="small" /> : <Lock fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Return to Default Position">
          <IconButton size="small" onClick={onReturnHome} sx={{ color: 'white' }}>
            <Home fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default ImageControlBar; 